(function () {
  'use strict';

  var Core = window.SwymWishlistCore;
  if (!Core) return;

  /** Skip refetch if we successfully loaded within this window (saves redundant Swym calls when toggling tabs). */
  var FRESH_CACHE_MS = 5000;
  /**
   * Hard timeout per Swym call — guards against a stuck spinner if neither callback fires,
   * and (in initSaveForLater) bounds how long we wait before giving up on an unconfirmed save.
   */
  var SWYM_CALL_TIMEOUT_MS = 8000;
  /** When false, next open of Saved tab will fetch. Never set true until a load finishes (avoids stuck empty UI). */
  var loaded = false;
  var savedReloadTimer = null;
  /** Invalidates in-flight Swym responses when a newer load started (tab / refresh). */
  var savedListLoadGen = 0;
  /**
   * Module-level cache of last successfully rendered items + product data.
   * Survives `form.Cart` re-renders triggered by `cart:refresh` (which wipes the saved panel DOM).
   * Lets us paint stale-but-correct items immediately on Saved tab open, even when Swym is unreachable
   * (e.g., relay blocked in incognito → status 0 errors).
   */
  var savedItemsCache = { items: null, products: null, ts: 0 };

  function hasFreshCache() {
    return (
      loaded &&
      savedItemsCache.items &&
      savedItemsCache.items.length > 0 &&
      Date.now() - savedItemsCache.ts < FRESH_CACHE_MS
    );
  }

  function paintFromCache(list, loading, empty) {
    if (!list || !savedItemsCache.items || !savedItemsCache.items.length) return false;
    var tab = document.querySelector('[data-cart-tab="saved"]');
    var priorLabel = tab ? tab.textContent : null;
    renderItems(list, savedItemsCache.items, savedItemsCache.products || {});
    // Restore label — renderItems overwrites it with stale cache count,
    // but an optimistic update may already show the correct newer count.
    if (tab && priorLabel) tab.textContent = priorLabel;
    if (loading) loading.style.display = 'none';
    if (empty) empty.style.display = 'none';
    return true;
  }

  function getCartDrawer() {
    return document.querySelector('#sidebar-cart');
  }

  function isSavedPanelVisible() {
    var cart = getCartDrawer();
    if (!cart) return false;
    var panel = cart.querySelector('[data-cart-panel="saved"]');
    if (!panel) return false;
    return window.getComputedStyle(panel).display !== 'none';
  }

  /**
   * Next open of Saved tab refetches. If Saved is visible, debounced reload so Swym has new data
   * (coalesces cart:refresh + swym:wishlist-updated).
   */
  function invalidateSavedCacheAndMaybeReload(delayMs) {
    loaded = false;
    var wait = typeof delayMs === 'number' ? delayMs : 480;
    clearTimeout(savedReloadTimer);
    savedReloadTimer = setTimeout(function () {
      savedReloadTimer = null;
      var cart = getCartDrawer();
      if (cart) {
        var list = cart.querySelector('.CartDrawer__SavedList');
        var loading = cart.querySelector('.CartDrawer__SavedLoading');
        var empty = cart.querySelector('.CartDrawer__SavedEmpty');
        if (list && !list.children.length) {
          paintFromCache(list, loading, empty);
        }
      }
      if (!isSavedPanelVisible()) return;
      onSwymReady(loadSavedItems);
    }, wait);
  }

  function onSwymReady(callback) {
    if (window._swat) {
      callback(window._swat);
      return;
    }
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }
    window.SwymCallbacks.push(callback);
  }

  // Resolve the active market's currency once from the [data-technical] blob
  // (ROW-950). Saved-tab money was hard-coded to '$', so an EUR market showed
  // the USD symbol; the numeric value already comes back market-correct
  // because the product JSON is fetched through Shopify.routes.root.
  // ROW-952: format the full amount with Intl(locale, currency) so separators
  // match the market (FR/EUR → 1.290,00-style), not US commas + symbol only.
  var _currency = null;
  function getCurrency() {
    if (_currency) return _currency;
    var code = 'USD';
    var locale = 'en';
    try {
      var el =
        window.document && window.document.querySelector('[data-technical]');
      if (el) {
        var data = JSON.parse(el.textContent);
        if (data.localization) {
          if (data.localization.currencyCode) {
            code = data.localization.currencyCode;
          }
          if (data.localization.locale) locale = data.localization.locale;
        }
      }
    } catch (e) {
      /* ignore — fall back to USD */
    }
    if (window.theme) {
      if (window.theme.currencyCode) code = window.theme.currencyCode;
      if (window.theme.language_iso_code) locale = window.theme.language_iso_code;
    }
    // EUR presentment: prefer de-DE separators (. thousands, , decimal) to
    // match Shopify amount_with_comma_separator / TLab EUR (ROW-952).
    if (code === 'EUR' && (!locale || locale === 'en' || locale.indexOf('en') === 0)) {
      locale = 'de-DE';
    }
    _currency = { code: code, locale: locale };
    return _currency;
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var cur = getCurrency();
    // theme.moneyFormat is only market-correct for EUR (leading € + comma
    // separators) and USD; for other live markets (GBP/JPY/KRW/HKD/CAD) it
    // falls back to the USD shop format and would render the wrong symbol.
    // Only take the formatString fast-path for those two; everything else
    // uses Intl below with the market's own currency/locale (ROW-952).
    var formatMatchesMarket = cur.code === 'EUR' || cur.code === 'USD';
    var formatString =
      (window.theme && window.theme.moneyFormat) || null;
    if (formatString && formatMatchesMarket) {
      var fromFormat = Core.formatMoneyFromFormat(cents, formatString);
      if (fromFormat != null) return fromFormat;
    }
    try {
      // No minimumFractionDigits override — Intl's own per-currency default
      // (2 for GBP/CAD/etc., 0 for JPY/KRW) always shows decimals for a
      // 2-decimal currency, matching the `| money` parity above (ROW-952).
      var formatted = new Intl.NumberFormat(cur.locale, {
        style: 'currency',
        currency: cur.code,
      }).format(cents / 100);
      return formatted;
    } catch (e) {
      return String(cents / 100);
    }
  }

  // Build a market/locale-prefixed product URL from a handle (ROW-950). The
  // saved-item link previously used Swym's add-time `du`, an absolute URL
  // frozen without the active market prefix (e.g. missing /en-fr/), which
  // could switch the customer's market mid-journey. Shopify.routes.root
  // carries the current prefix, matching how the Bag tab's Liquid URLs render.
  function buildProductUrl(handle) {
    if (!handle) return '#';
    var root =
      (window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root) ||
      '/';
    if (root.slice(-1) !== '/') root += '/';
    return root + 'products/' + encodeURIComponent(handle);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function extractHandle(url) {
    if (!url) return null;
    var match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function formatTitle(title) {
    var parts = title.split(' :: ');
    return parts[parts.length - 1];
  }

  function getOptionName(option) {
    if (typeof option === 'string') return option.toLowerCase();
    if (option && option.name) return option.name.toLowerCase();
    return '';
  }

  function getVariantMeta(product, variant) {
    if (!product || !variant || !product.options) return [];

    var colorLabels = ['color', 'colour', 'couleur', 'colore', 'farbe'];
    var sizeLabels = ['size', 'taille', 'taglia', 'größe'];
    var color = '';
    var size = '';

    for (var i = 0; i < product.options.length; i++) {
      var name = getOptionName(product.options[i]);
      if (!color && colorLabels.indexOf(name) !== -1) {
        color = variant['option' + (i + 1)] || '';
      }
      if (!size && sizeLabels.indexOf(name) !== -1) {
        size = variant['option' + (i + 1)] || '';
      }
    }

    var parts = [];
    if (color) parts.push('Color: ' + color);
    if (size) parts.push('Size: ' + size);
    return parts;
  }

  function fetchProduct(handle) {
    return fetch(Core.productsJsonUrl(handle))
      .then(function (res) {
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .catch(function () {
        return null;
      });
  }

  // ─── Tab switching ───

  /**
   * Mini-cart section re-render replaces only `form.Cart`, not the header with tabs.
   * Sync tab state + panels so «Saved» active class cannot outlive a fresh bag DOM.
   */
  function setCartDrawerTab(cart, target) {
    if (!cart || (target !== 'bag' && target !== 'saved')) return;

    var tabs = cart.querySelectorAll('[data-cart-tab]');
    var bagPanel = cart.querySelector('.Drawer__Main');
    var savedPanel = cart.querySelector('[data-cart-panel="saved"]');
    var cartEmpty = cart.querySelector('.Cart__Empty');
    var cartFooter = cart.querySelector('.Drawer__Footer');
    var pickupNotice = cart.querySelector('.Cart__PickupShippingNotice');
    var checkoutBtn = cart.querySelector('.Cart__Checkout--Submit');
    var viewBagLink = cart.querySelector('.Button--View_Bag');
    if (!savedPanel) {
      // Without a saved panel to show, a stale 'saved' state would hide the Bag panel
      // via CSS with nothing to replace it — never leave the drawer with both hidden.
      cart.classList.remove('Drawer--saved-active');
      return;
    }

    var activeTab = cart.querySelector('[data-cart-tab="' + target + '"]');
    tabs.forEach(function (t) {
      t.classList.toggle('CartDrawer__Tab--active', t === activeTab);
      t.classList.toggle('CartDrawer__Tab--inactive', t !== activeTab);
    });

    var isSaved = target === 'saved';
    var hiddenDisplay = 'none';
    if (bagPanel) bagPanel.style.display = isSaved ? 'none' : '';
    if (cartEmpty) cartEmpty.style.display = isSaved ? 'none' : '';
    if (cartFooter) cartFooter.style.display = isSaved ? 'none' : '';
    if (pickupNotice) pickupNotice.style.display = isSaved ? 'none' : '';
    if (checkoutBtn) checkoutBtn.style.display = isSaved ? 'none' : '';
    if (viewBagLink) viewBagLink.closest('a') && (viewBagLink.closest('a').style.display = isSaved ? 'none' : '');
    savedPanel.style.display = isSaved ? 'flex' : 'none';
    cart.classList.toggle('Drawer--saved-active', isSaved);
  }

  function resetCartDrawerToBagTab() {
    var cart = document.querySelector('#sidebar-cart');
    if (cart) setCartDrawerTab(cart, 'bag');
  }

  function initTabs() {
    document.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-cart-tab]');
      if (!tab) return;

      var target = tab.getAttribute('data-cart-tab');
      var cart = document.querySelector('#sidebar-cart');
      if (!cart) return;

      if (!cart.querySelector('[data-cart-panel="saved"]')) return;

      setCartDrawerTab(cart, target);

      if (target === 'saved') {
        // Always fetch when opening Saved — a prior load may have failed, returned empty v3
        // before legacy fallback, or raced with cart:refresh; `loaded` must not block retries.
        onSwymReady(loadSavedItems);
      }
    });
  }

  /** Drawer close re-reloads cart HTML without `cart:refresh`; keep tabs in sync. */
  function watchCartDrawerCloseForTabReset() {
    var cart = document.querySelector('#sidebar-cart');
    if (!cart) return;
    var observer = new MutationObserver(function (mutations) {
      var ariaChanged = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'aria-hidden') {
          ariaChanged = true;
          break;
        }
      }
      if (ariaChanged && cart.getAttribute('aria-hidden') === 'true') {
        resetCartDrawerToBagTab();
      }
    });
    observer.observe(cart, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  // ─── Save for Later (from Bag tab) ───

  /**
   * Blocks checkout while any Save-for-Later write is pending, so a shopper can't check out
   * with an item that's about to be silently removed once the wishlist write confirms — the
   * cart removal itself is deferred until then (see initSaveForLater), so this is the only
   * gap that needs closing.
   *
   * `cart:refresh` can trigger a full replacement of `form.Cart` (e.g. a second concurrent
   * save confirming, the drawer closing/reopening, adding from the Saved tab) — that swaps in
   * a fresh, non-disabled checkout button from the server markup. A MutationObserver re-asserts
   * the block on any such change instead of trying to enumerate every event that could cause it.
   */
  var pendingSaveForLaterCount = 0;
  var checkoutBlockObserverStarted = false;

  function setCheckoutBlocked(blocked) {
    var checkoutBtn = document.querySelector('.Cart__Checkout--Submit');
    if (checkoutBtn) checkoutBtn.disabled = blocked;
  }

  function ensureCheckoutBlockObserver() {
    if (checkoutBlockObserverStarted) return;
    var cart = document.querySelector('#sidebar-cart');
    if (!cart) return;
    checkoutBlockObserverStarted = true;
    new MutationObserver(function () {
      if (pendingSaveForLaterCount > 0) setCheckoutBlocked(true);
    }).observe(cart, { childList: true, subtree: true });
  }

  function beginPendingSave() {
    pendingSaveForLaterCount++;
    setCheckoutBlocked(true);
  }

  function endPendingSave() {
    pendingSaveForLaterCount = Math.max(0, pendingSaveForLaterCount - 1);
    if (pendingSaveForLaterCount === 0) setCheckoutBlocked(false);
  }

  // In-flight guard keyed by variant id rather than a DOM attribute on the clicked button —
  // a `cart:refresh` full re-render (drawer close/reopen, a sibling save confirming, etc.)
  // replaces the button node entirely, which would silently drop a per-node flag and let a
  // second click start a duplicate flow for the same line.
  var pendingSaveVariantIds = {};

  function initSaveForLater() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.CartItem__SaveForLater');
      if (!btn) return;

      var productId = parseInt(btn.getAttribute('data-product-id'), 10);
      var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
      var productUrl = btn.getAttribute('data-product-url');

      if (pendingSaveVariantIds[variantId]) return; // already saving this line — ignore repeat clicks
      pendingSaveVariantIds[variantId] = true;

      ensureCheckoutBlockObserver();
      beginPendingSave();

      // Optimistic: hide the cart item immediately
      var cartItemWrapper = btn.closest('.CartItemWrapper') || btn.closest('.CartItem');
      if (cartItemWrapper) cartItemWrapper.style.display = 'none';

      // Optimistically bump saved count — read from tab label (always up-to-date)
      var currentSavedCount = getSavedTabCount();
      updateSavedTabLabel(currentSavedCount + 1);

      var settled = false;

      // The cart line is never touched until the wishlist write is confirmed, so giving up
      // just means undoing the optimistic UI — there's no line-item state (properties,
      // selling plan, quota tags) to reconstruct, because nothing was ever removed.
      function revert() {
        if (cartItemWrapper) cartItemWrapper.style.display = '';
        // Undo only THIS save's optimistic +1 relative to the live label. Restoring the
        // click-time snapshot (currentSavedCount) would clobber a concurrent save's bump.
        updateSavedTabLabel(Math.max(0, getSavedTabCount() - 1));
        delete pendingSaveVariantIds[variantId];
        endPendingSave();
      }

      // Fail-safe: if the wishlist write never confirms (Swym SDK slow/blocked on mobile),
      // give up and restore the item. Checkout stays blocked for this whole window (see
      // beginPendingSave above), so there's no gap where the item reads as saved while it's
      // still actually purchasable.
      var timeoutId = setTimeout(function () {
        if (settled) return;
        settled = true;
        revert();
      }, SWYM_CALL_TIMEOUT_MS);

      onSwymReady(function (swat) {
        if (settled) return; // already timed out — don't reach into Swym after the user saw it restored
        swat.addToWishList(
          {
            epi: variantId,
            empi: productId,
            du: window.location.origin + productUrl,
          },
          function () {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            loaded = false;
            try {
              document.documentElement.dispatchEvent(
                new CustomEvent('swym:wishlist-updated', { bubbles: true }),
              );
            } catch (e) { /* ignore */ }

            // Only remove from cart once the wishlist write is confirmed. This fetch has its
            // own abort-timeout: without it, a hung /cart/change.js request (slow mobile) would
            // leave checkout disabled and the row hidden until a full page reload, since
            // endPendingSave() only runs on settle.
            var cartController = new AbortController();
            var cartTimeoutId = setTimeout(function () {
              cartController.abort();
            }, SWYM_CALL_TIMEOUT_MS);
            fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: String(variantId), quantity: 0 }),
              signal: cartController.signal,
            })
              .then(function (res) {
                clearTimeout(cartTimeoutId);
                if (!res.ok) throw new Error('cart/change.js failed');
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:refresh', { bubbles: true }),
                );
                delete pendingSaveVariantIds[variantId];
                endPendingSave();
              })
              .catch(function () {
                clearTimeout(cartTimeoutId);
                // The wishlist write already succeeded (we're past that callback) — don't
                // revert the Saved count, the item really is saved. Just restore the visible
                // row (removal failed or timed out, so it's still genuinely in the cart) and
                // always unblock checkout so it can never stay stuck disabled.
                if (cartItemWrapper) cartItemWrapper.style.display = '';
                delete pendingSaveVariantIds[variantId];
                endPendingSave();
              });
          },
          function () {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            revert();
          },
        );
      });
    });
  }

  // Known ceiling: the actual /cart/change.js removal only fires after Swym confirms, so if the
  // shopper navigates away in that window (or Swym's relay confirms after we've already given up
  // at the 8s timeout), the removal can be skipped or arrive too late to matter. Either way the
  // cart line is never destroyed early, so the outcome is at worst "saved to the wishlist and
  // still in the cart" (recoverable, self-corrects once the Saved tab is reopened) — never the
  // silent data loss this ticket reported. We can't cancel an in-flight Swym call (fire-and-forget
  // API) or a page unload, so this residual window isn't fully closeable from theme code.

  // ─── Load saved items from Swym ───

  function loadSavedItems(swat) {
    var myGen = ++savedListLoadGen;

    function stillCurrent() {
      return myGen === savedListLoadGen;
    }

    var cart = getCartDrawer();
    var loading = cart
      ? cart.querySelector('.CartDrawer__SavedLoading')
      : document.querySelector('#sidebar-cart .CartDrawer__SavedLoading');
    var empty = cart
      ? cart.querySelector('.CartDrawer__SavedEmpty')
      : document.querySelector('#sidebar-cart .CartDrawer__SavedEmpty');
    var list = cart
      ? cart.querySelector('.CartDrawer__SavedList')
      : document.querySelector('#sidebar-cart .CartDrawer__SavedList');

    // If `form.Cart` was re-rendered (via cart:refresh), DOM is empty — paint cache before fetch.
    if (list && !list.children.length) {
      paintFromCache(list, loading, empty);
    }

    var hadPriorContent = !!(list && list.children.length);

    // Skip redundant network calls when the user just toggles tabs and we have a fresh successful render.
    if (hasFreshCache() && hadPriorContent) {
      return;
    }

    // Only show the spinner on first load. If items are already rendered (DOM or cache),
    // leave them visible while we refetch — error paths must not wipe a working UI.
    if (!hadPriorContent) {
      if (loading) loading.style.display = '';
      if (empty) empty.style.display = 'none';
    }

    // Fail-safe: never leave the spinner up indefinitely if Swym callbacks are dropped.
    setTimeout(function () {
      if (!stillCurrent()) return;
      if (loaded) return;
      showEmptyAfterFailure();
    }, SWYM_CALL_TIMEOUT_MS);

    /** Confirmed empty list from Swym (not a network failure). */
    function showEmptyState() {
      if (!stillCurrent()) return;
      if (loading) loading.style.display = 'none';
      if (list) list.innerHTML = '';
      if (empty) empty.style.display = '';
      updateSavedTabLabel(0);
      savedItemsCache = { items: null, products: null, ts: Date.now() };
      loaded = true;
    }

    /**
     * Network/SDK failure (e.g. Swym relay blocked in incognito → status 0).
     * Preserve any prior rendered content rather than wiping to "empty" — error ≠ empty.
     * If DOM is empty but we have cache, restore from cache as a last resort.
     */
    function showEmptyAfterFailure() {
      if (!stillCurrent()) return;
      if (loading) loading.style.display = 'none';
      loaded = false;
      if (hadPriorContent) return;
      if (paintFromCache(list, loading, empty)) return;
      if (empty) empty.style.display = '';
    }

    function renderSavedFromItems(items) {
      if (!stillCurrent()) return;
      if (!items || items.length === 0) {
        showEmptyState();
        return;
      }

      var recent = items.slice();

      var handles = [];
      var products = {};

      recent.forEach(function (item) {
        var handle = extractHandle(item.du);
        if (handle && handles.indexOf(handle) === -1) {
          handles.push(handle);
        }
      });

      Promise.all(
        handles.map(function (handle) {
          return fetchProduct(handle).then(function (product) {
            if (product) {
              products[product.id] = product;
              products[String(product.id)] = product;
            }
          });
        }),
      ).then(function () {
        if (!stillCurrent()) return;
        if (loading) loading.style.display = 'none';
        renderItems(list, recent, products);
        savedItemsCache = { items: recent, products: products, ts: Date.now() };
        loaded = true;
      });
    }

    function tryLegacyThenEmpty() {
      if (
        !Core.invokeFetchWishlist(
          swat,
          renderSavedFromItems,
          showEmptyAfterFailure,
        )
      ) {
        showEmptyAfterFailure();
      }
    }

    /**
     * addToWishList from PDP vs “Save for Later” can live on different Swym lids (wl vs _saveForLater).
     * Merge every list’s rows (same ordering as swym-wishlist: non–save-for-later first), dedupe by variant,
     * then renderSavedFromItems takes the last MAX_ITEMS — so cart “Saved” matches /pages/wishlist content.
     */
    function loadMergedFromAllLists(ordered) {
      var merged = [];

      function step(index) {
        if (!stillCurrent()) return;
        if (index >= ordered.length) {
          if (merged.length === 0) {
            tryLegacyThenEmpty();
            return;
          }
          renderSavedFromItems(Core.dedupeWishlistRowsKeepLast(merged));
          return;
        }
        var listMeta = ordered[index];
        if (!listMeta || !listMeta.lid) {
          step(index + 1);
          return;
        }
        swat.fetchListDetails(
          { lid: listMeta.lid },
          function (response) {
            if (!stillCurrent()) return;
            merged = merged.concat(Core.extractListItems(response));
            step(index + 1);
          },
          function () {
            step(index + 1);
          },
        );
      }

      step(0);
    }

    swat.fetchLists({
      callbackFn: function (rawLists) {
        if (!stillCurrent()) return;
        var lists = Core.normalizeFetchListsPayload(rawLists);
        if (!lists.length) {
          tryLegacyThenEmpty();
          return;
        }

        var withLid = lists.filter(function (l) {
          return l && l.lid;
        });
        if (!withLid.length) {
          tryLegacyThenEmpty();
          return;
        }

        withLid.sort(function (a, b) {
          return (
            (Core.isSaveForLaterList(a) ? 1 : 0) -
            (Core.isSaveForLaterList(b) ? 1 : 0)
          );
        });

        loadMergedFromAllLists(withLid);
      },
      errorFn: function () {
        tryLegacyThenEmpty();
      },
    });
  }

  /**
   * Keep module cache in sync when an item leaves Saved (Add to Bag).
   * cart:refresh replaces `form.Cart` and clears CartDrawer__SavedList; paintFromCache
   * would otherwise restore a stale list that still includes the variant.
   */
  function removeItemFromSavedCache(epi) {
    var epiStr = String(epi);
    if (!savedItemsCache.items || !savedItemsCache.items.length) {
      return 0;
    }

    savedItemsCache.items = savedItemsCache.items.filter(function (item) {
      return String(item.epi) !== epiStr;
    });
    savedItemsCache.ts = Date.now();
    loaded = false;

    if (savedItemsCache.items.length === 0) {
      savedItemsCache.items = null;
      savedItemsCache.products = null;
      return 0;
    }

    return savedItemsCache.items.length;
  }

  // ─── Render items ───

  function renderItems(container, items, products) {
    if (!container) return;
    container.innerHTML = '';

    items.forEach(function (item) {
      var product =
        products[item.empi] || products[String(item.empi)] || null;
      var variant = null;

      if (product && product.variants) {
        variant =
          product.variants.find(function (v) {
            return String(v.id) === String(item.epi);
          }) || null;
      }

      var title = formatTitle(product ? product.title : item.dt || '');
      // Only render a price when we have market-correct product JSON (fetched
      // through Shopify.routes.root). Swym's add-time item.pr is frozen in
      // whatever currency was active at save time, so showing it on another
      // market would be a wrong value under the active currency symbol —
      // worse than showing nothing. Empty string renders no price (ROW-950).
      var price = product
        ? formatMoney(variant ? variant.price : product.price)
        : '';
      var imgUrl = item.iu || '';
      if (product && product.featured_image) {
        imgUrl = product.featured_image;
      }
      var handle = (product && product.handle) || extractHandle(item.du);
      var productUrl = handle ? buildProductUrl(handle) : item.du || '#';
      var metaParts = product && variant ? getVariantMeta(product, variant) : [];
      if (!metaParts.length && item.vi) {
        var viParts = item.vi.split(/\s*\/\s*/);
        if (viParts[0]) metaParts.push('Color: ' + viParts[0]);
        if (viParts[1]) metaParts.push('Size: ' + viParts[1]);
      }

      var html = '<div class="CartDrawer__SavedItem">';
      html +=
        '<div class="CartDrawer__SavedItem__ImageWrapper">' +
        '<a class="CartDrawer__SavedItem__ImageLink" href="' + escapeAttr(productUrl) + '">' +
        '<img class="CartDrawer__SavedItem__Image" src="' + escapeAttr(imgUrl) + '" alt="' + escapeAttr(title) + '" loading="lazy">' +
        '</a>' +
        '</div>';
      html += '<div class="CartDrawer__SavedItem__Info">';
      html += '<div class="CartDrawer__SavedItem__InfoWrapper">';
      html +=
        '<h2 class="CartItem__Title Heading"><a class="CartDrawer__SavedItem__TitleLink" href="' +
        escapeAttr(productUrl) +
        '">' +
        escapeHtml(title) +
        '</a></h2>';
      html += '<div class="CartDrawer__SavedItem__PriceWrapper"><span class="CartDrawer__SavedItem__Price Price">' + price + '</span></div>';
      html += '</div>';

      html += '<div class="CartDrawer__SavedItem__MetaWrapper">';
      metaParts.forEach(function (meta) {
        html +=
          '<p class="CartItem__Meta Heading Text--subdued" style="margin: 0">' +
          escapeHtml(meta) +
          '</p>';
      });

      html += '<p class="CartItem__Meta Heading Text--subdued" style="margin: 0">Qty: 1</p>';
      html += '</div>';

      var btnState = Core.getButtonState(product, variant);
      html +=
        '<button type="button" class="CartDrawer__SavedAddToBag" data-epi="' +
        escapeAttr(String(item.epi)) +
        '" data-empi="' +
        escapeAttr(String(item.empi != null ? item.empi : '')) +
        '" data-du="' +
        escapeAttr(item.du || '') +
        '" data-btn-type="' +
        escapeAttr(btnState.type) +
        '" data-preorder-props="' +
        escapeAttr(Core.preOrderPropertiesAttr(product)) +
        '" data-selling-plan="' +
        escapeAttr(Core.sellingPlanAttr(variant)) +
        '"' +
        (btnState.enabled ? '' : ' disabled') +
        '>' +
        escapeHtml(btnState.text) +
        '</button>';
      html += '</div>';
      html += '</div>';

      container.insertAdjacentHTML('beforeend', html);
    });

    updateSavedTabLabel(items.length);

    // Bind CTA buttons (add / pre-order / notify)
    container.querySelectorAll('.CartDrawer__SavedAddToBag').forEach(function (btn) {
      if (btn.disabled) return;
      var btnType = btn.getAttribute('data-btn-type') || 'add';
      btn.addEventListener('click', function () {
        var epi = parseInt(btn.getAttribute('data-epi'), 10);
        if (btnType === 'notify') {
          Core.openNotifyMeForVariant(String(epi));
          return;
        }
        addToBag(epi, btn);
      });
    });
  }

  function addToBag(variantId, btn) {
    btn.textContent = 'Adding...';
    btn.disabled = true;

    var card = btn.closest('.CartDrawer__SavedItem');
    var savedList = document.querySelector('.CartDrawer__SavedList');
    var currentSavedCount = savedList
      ? savedList.querySelectorAll('.CartDrawer__SavedItem').length
      : savedItemsCache.items
        ? savedItemsCache.items.length
        : 0;

    var remainingCount = removeItemFromSavedCache(variantId);
    if (remainingCount === 0 && currentSavedCount <= 1) {
      updateSavedTabLabel(0);
      if (savedList) savedList.innerHTML = '';
      var emptyEl = document.querySelector('#sidebar-cart .CartDrawer__SavedEmpty');
      if (emptyEl) emptyEl.style.display = '';
    } else {
      updateSavedTabLabel(
        remainingCount > 0 ? remainingCount : Math.max(0, currentSavedCount - 1),
      );
    }

    if (card) card.remove();

    var duAttr = btn.getAttribute('data-du') || '';
    var swymPayload = {
      epi: variantId,
      epiStr: String(variantId),
      empi: parseInt(btn.getAttribute('data-empi'), 10),
      du: Core.absoluteProductUrl(duAttr),
    };

    // Stamp before fetch so the cart/add interceptor skips this variant.
    if (window.dataLayerTracking) {
      window.dataLayerTracking.markPendingExplicitAtc(String(variantId));
    }

    var addPayload = { id: String(variantId), quantity: 1 };
    var preOrderProps = Core.readPreOrderProperties(btn);
    if (preOrderProps) addPayload.properties = preOrderProps;
    var sellingPlan = Core.readSellingPlan(btn);
    if (sellingPlan) addPayload.selling_plan = sellingPlan;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(addPayload),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .then(function (cartItem) {
        if (window.dataLayerTracking) {
          window.dataLayerTracking.trackAddToCart(
            {
              product_id: cartItem.product_id,
              product_title: cartItem.product_title || cartItem.title,
              variant_id: cartItem.variant_id || cartItem.id,
              variant_title: cartItem.variant_title,
              price: cartItem.price,
              vendor: cartItem.vendor,
              product_type: cartItem.product_type,
              sku: cartItem.sku,
              quantity: cartItem.quantity || 1,
            },
            {
              source: 'cart_saved',
              method: 'button_click',
            },
          );
        }

        // Remove from Swym async — don't block the refresh
        onSwymReady(function (swat) {
          loaded = false;
          Core.removeVariantFromSwym(swat, swymPayload, function () {
            try {
              document.documentElement.dispatchEvent(
                new CustomEvent('swym:wishlist-updated', { bubbles: true }),
              );
            } catch (e) { /* ignore */ }
          }, function () { /* silent */ });
        });
        // Refresh cart immediately
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true }),
        );
      })
      .catch(function () {
        loaded = false;
        invalidateSavedCacheAndMaybeReload(0);
        updateSavedTabLabel(currentSavedCount);
        var btnType = btn.getAttribute('data-btn-type') || 'add';
        btn.textContent =
          btnType === 'preorder'
            ? 'Pre-Order'
            : btnType === 'notify'
              ? (window.swymWishlistStrings && window.swymWishlistStrings.notifyMe) ||
                'Notify Me'
              : 'Add to Bag';
        btn.disabled = false;
      });
  }

  function getSavedTabCount() {
    var tab = document.querySelector('[data-cart-tab="saved"]');
    if (!tab) return 0;
    var m = tab.textContent.match(/\((\d+)\)/);
    return m ? parseInt(m[1], 10) : 0;
  }

  function updateSavedTabLabel(count) {
    var tab = document.querySelector('[data-cart-tab="saved"]');
    if (!tab) return;
    tab.textContent = count > 0 ? 'Saved (' + count + ')' : 'Saved';
  }

  // ─── Cart count sync ───

  var cartCountTimer = null;

  function syncBagTabFromNavCount() {
    var countEl = document.querySelector('.Header__CartCount');
    var bagTab = document.querySelector('[data-cart-tab="bag"]');
    if (!bagTab) return;
    var n = countEl ? (parseInt(countEl.textContent, 10) || 0) : null;
    if (n !== null) {
      bagTab.textContent = n > 0 ? 'Bag (' + n + ')' : 'Bag';
    }
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var bagTab = document.querySelector('[data-cart-tab="bag"]');
        if (bagTab) {
          bagTab.textContent = cart.item_count > 0 ? 'Bag (' + cart.item_count + ')' : 'Bag';
        }
      })
      .catch(function () {});
  }

  // Watch .Header__CartCount for mutations — theme.js updates it on every _replaceContent
  // (covers quantity changes and item removals that don't dispatch cart:refresh).
  (function watchNavCartCount() {
    var target = document.querySelector('.Header__CartCount');
    if (!target) return;
    new MutationObserver(function () {
      syncBagTabFromNavCount();
    }).observe(target, { childList: true, characterData: true, subtree: true });
  })();

  // Debounced listener for cart:refresh + invalidate Saved list (Save for Later, line-item changes)
  document.documentElement.addEventListener('cart:refresh', function () {
    resetCartDrawerToBagTab();
    invalidateSavedCacheAndMaybeReload(550);
    clearTimeout(cartCountTimer);
    cartCountTimer = setTimeout(updateCartCount, 500);
  });

  // product:added fires when ATC succeeds; _rerenderCart updates bag count via section HTML.
  document.addEventListener('product:added', function () {
    invalidateSavedCacheAndMaybeReload(450);
  });

  // Full mini-cart HTML swaps wipe CartDrawer__SavedList; repaint from module cache when possible.
  document.documentElement.addEventListener('cart:drawer-html-replaced', function () {
    var cart = getCartDrawer();
    if (!cart) return;
    var list = cart.querySelector('.CartDrawer__SavedList');
    var loading = cart.querySelector('.CartDrawer__SavedLoading');
    var empty = cart.querySelector('.CartDrawer__SavedEmpty');
    if (list && !list.children.length) {
      paintFromCache(list, loading, empty);
    }
    if (isSavedPanelVisible()) {
      invalidateSavedCacheAndMaybeReload(120);
    }
  });

  document.documentElement.addEventListener('swym:wishlist-updated', function (e) {
    if (e.detail && typeof e.detail.count === 'number') {
      updateSavedTabLabel(e.detail.count);
    }
    invalidateSavedCacheAndMaybeReload(450);
  });

  // Wishlist page / PDP heart sync — always refresh tab label when count is provided.
  document.documentElement.addEventListener('swym:wishlist-count', function (e) {
    var count = e.detail && typeof e.detail.count === 'number' ? e.detail.count : null;
    if (count === null) return;
    updateSavedTabLabel(count);
  });

  // ─── Boot ───

  function boot() {
    initTabs();
    initSaveForLater();
    watchCartDrawerCloseForTabReset();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
